<script setup lang="ts">
import { api } from '@/api';
import {
  AppPermissionsEnum,
  type EnumerationQuantityDto,
  type EnumerationQuantityUsageDto,
} from '@/api/generator';
import PtwAddButton from '@/components/buttons/PtwAddButton.vue';
import { getDefaultDeleteConfirmationOptions } from '@/components/confirmationModals/defaults';
import { EntityInfo } from '@/components/confirmationModals/types';
import { useActionWithConfirmation } from '@/components/confirmationModals/useActionWithConfirmation';
import { TAB_PREFIX } from '@/components/confirmationModals/useFormConfirmationModal';
import ContextMenu from '@/components/ContextMenu/ContextMenu.vue';
import { ContextMenuAction } from '@/components/ContextMenu/types';
import {
  getDefaultContextMenuActions,
  globalContextMenuHeaderItemDefinition,
} from '@/components/ContextMenu/utils';
import EntityChange from '@/components/EntityHistory/EntityChange.vue';
import CommentCount from '@/components/forms/CommentCount/CommentCount.vue';
import { EMPTY_FIELD_SIGN } from '@/components/forms/utils.ts';
import IdentifierLabel from '@/components/IdentifierLabel/IdentifierLabel.vue';
import OverviewTable from '@/components/OverviewTable/OverviewTable.vue';
import OverviewTableHeader from '@/components/OverviewTable/OverviewTableHeader.vue';
import type { SortItem } from '@/components/OverviewTable/types';
import BasePage from '@/components/pages/BasePage/BasePage.vue';
import BasePageTitle from '@/components/pages/BasePage/BasePageTitle.vue';
import { routenames } from '@/router/routenames';
import { alphabeticalStringCompare, localizedEnumCompare } from '@/utils/compareAndSort';
import { translateEntityProperty, truthy } from '@/utils/helper';
import { buildOdataQuery } from '@/utils/odata';
import { getTranslationPrefix } from '@/utils/translationPrefix';
import type { I18nKey } from '@/utils/typings';
import { useLoading } from '@/utils/useLoading';
import { useTranslation } from 'i18next-vue';
import { ref, watchEffect, useTemplateRef } from 'vue';
import { useRouter } from 'vue-router';
import EnumerationOverviewFilter from './components/EnumerationOverviewFilter.vue';
import { EnumerationOverviewFilterObject, ScopeFilterObject } from './types';
import { getEntityChangeEvent } from '@/components/EntityHistory/entityChange';
import { useRemainingViewportHeight } from '@/utils/useRemainingVueport';
import { usePermissionsStore } from '@/stores/permissionsStore.ts';
import { useTableScrollElement } from '@/utils/vuetifyMenuHelper';

const { t } = useTranslation();
const { hasPermissions } = usePermissionsStore();
const tableRef = useTemplateRef<HTMLDivElement>('table');
const { height } = useRemainingViewportHeight(tableRef);
const tableScrollElement = useTableScrollElement(tableRef);

const headers = [
  {
    title: ``,
    key: 'name',
    sortRaw(enumA: EnumerationQuantityDto, enumB: EnumerationQuantityDto) {
      const translatedEnumNameA = translateEntityProperty('name', enumA);
      const translatedEnumNameB = translateEntityProperty('name', enumB);
      return translatedEnumNameA && translatedEnumNameB
        ? alphabeticalStringCompare(translatedEnumNameA, translatedEnumNameB)
        : -1;
    },
  },
  {
    title: t('UseInEvaluation_other'),
    key: 'useInEvaluations',
    sortable: false,
  },
  {
    title: t('UseInSetup_other'),
    key: 'useInSetups',
    sortable: false,
  },
  {
    title: t('StateOfUse'),
    key: 'state',
    sort: localizedEnumCompare('StateOfUseEnum'),
  },
  {
    title: t('Added'),
    key: 'creationTime',
  },
  {
    title: t('LastModification'),
    key: 'lastModificationTime',
  },
  globalContextMenuHeaderItemDefinition,
] as const;

const filterObject = ref<EnumerationOverviewFilterObject>({});

const initialData = ref<EnumerationQuantityDto[]>();

const enumerations = ref<EnumerationQuantityDto[]>();

const router = useRouter();

const sortBy: SortItem[] = [{ key: 'name', order: 'asc' }];

function getScopeFilter(filter: ScopeFilterObject[]) {
  return {
    or: [
      ...filter.map((entry) => ({
        [entry.usage]: {
          [entry.scope]: {
            any: { [entry.property]: entry.option },
          },
        },
      })),
    ],
  };
}

const {
  action: loadEnumerations,
  loading,
  loadingFirstTime,
} = useLoading(async () => {
  const query = buildOdataQuery({
    search: filterObject.value?.search || undefined,
    filter: {
      and: [filterObject.value.evaluationUsage, filterObject.value.setupUsage]
        .filter(truthy)
        .map(getScopeFilter),
    },
  });

  const { result } = await api.enumerationQuantitiesApi.getEnumerationQuantityList(query);

  if (result?.value) {
    enumerations.value = result.value;

    // The unfiltered list also provides the filter options, so no extra request is needed.
    // While a filter is active we keep the options from the last unfiltered load.
    const { search, evaluationUsage, setupUsage } = filterObject.value;
    if (!search && !evaluationUsage?.length && !setupUsage?.length) {
      initialData.value = result.value;
    }
  }
}, true);

function navigateTo(id: string, hash?: string) {
  router.push({
    name: routenames.enumerations.details,
    hash,
    params: { id },
  });
}

const { action: deleteAction } = useActionWithConfirmation({
  confirmedAction: async (id: string) => {
    const { success } = await api.enumerationQuantitiesApi.deleteEnumerationQuantity({ id });

    if (success) {
      loadEnumerations();
    }
  },
  confirmationOptions: {
    title: 'Really',
    text: 'Do you want to delete this Enumeration?',
  },
});

function getContextMenuActions(item: EnumerationQuantityDto): ContextMenuAction[] {
  const defaultContextMenuActions = getDefaultContextMenuActions(item);
  return [
    {
      ...defaultContextMenuActions.view,
      to: {
        name: routenames.enumerations.details,
        params: { id: item.id },
      },
    },
    {
      ...defaultContextMenuActions.edit,
      to: {
        name: routenames.enumerations.edit,
        params: { id: item.id },
      },
    },
    {
      ...defaultContextMenuActions.delete,
      callback: () => {
        deleteAction({
          param: item.id,
          confirmationOptions: getDefaultDeleteConfirmationOptions(
            {
              ...(item as EntityInfo),
              typeName: 'EnumerationItem_one',
            },
            t,
          ),
        });
      },
    },
  ];
}

function listUsage(usage: EnumerationQuantityUsageDto | undefined) {
  const medicalSystemScopes = (usage?.medicalSystemScope || []).map((scope) => {
    if (scope.rtSystemSubCategory) {
      return t(
        `${getTranslationPrefix('medicalSystemScope', 'rtSystemSubCategory')}.${
          scope.rtSystemSubCategory
        }` as I18nKey,
      );
    } else {
      return t(
        `${getTranslationPrefix('medicalSystemScope', 'rtSystemCategory')}.${
          scope.rtSystemCategory
        }` as I18nKey,
      );
    }
  });

  const qaEquipmentScopes = (usage?.qaEquipmentScope || []).map((scope) =>
    t(`QaEquipmentSubCategoryEnum.${scope.rtSystemSubCategory}` as I18nKey),
  );
  return [...medicalSystemScopes.filter(truthy), ...qaEquipmentScopes.filter(truthy)].join(', ');
}

watchEffect(loadEnumerations);
</script>

<template>
  <BasePage :loading="loadingFirstTime">
    <template #title>
      <BasePageTitle :title="$t('Enumeration_other')" />
    </template>

    <template #actions>
      <PtwAddButton
        v-if="hasPermissions([AppPermissionsEnum.GlobalEnumerationsAdd])"
        :to="{ name: routenames.enumerations.add }"
      />
    </template>

    <EnumerationOverviewFilter v-model="filterObject" :initialData="initialData" />
    <OverviewTable
      ref="table"
      class="mt-2"
      :headers="headers"
      :items="enumerations"
      itemLabel="Enumeration"
      data-testid="DataTable_Enumeration"
      :loading="loading"
      :sortBy="sortBy"
      :height="height"
      fixedHeader
      @click:row="
        (_event, data) => {
          navigateTo(data.item.id);
        }
      "
    >
      <template #header.name="headerProps">
        <OverviewTableHeader v-bind="headerProps">
          {{ $t('Name') }}
          <span class="font-weight-medium">| {{ $t('ID') }}</span>
        </OverviewTableHeader>
      </template>

      <template #header.useInEvaluations="headerProps">
        <OverviewTableHeader v-bind="headerProps">
          {{ $t('UseInEvaluation_other') }} <br />
          <span class="font-weight-medium">{{ $t('TaskCategory_other') }}</span>
        </OverviewTableHeader>
      </template>

      <template #header.useInSetups="headerProps">
        <OverviewTableHeader v-bind="headerProps">
          {{ $t('UseInSetup_other') }} <br />
          <span class="font-weight-medium">
            {{ $t('SetupCategory_other') }}
          </span>
        </OverviewTableHeader>
      </template>

      <template #item.name="{ item }">
        <IdentifierLabel :identifier="item.iuid" :label="translateEntityProperty('name', item)" />
        <CommentCount :value="item" @click.stop="navigateTo(item.id, `#${TAB_PREFIX}comments`)" />
      </template>

      <template #item.useInEvaluations="{ item }">
        {{ listUsage(item.evaluationUsage) || EMPTY_FIELD_SIGN }}
      </template>

      <template #item.useInSetups="{ item }">
        {{ listUsage(item.setupUsage) || EMPTY_FIELD_SIGN }}
      </template>

      <template #item.state="{ item }">
        {{ $t(`StateOfUseEnum.${item.state}` as I18nKey) }}
        <EntityChange
          :entityChange="getEntityChangeEvent(item, 'editing')"
          :showEmptyFieldSign="false"
        />
      </template>

      <template #item.creationTime="{ item }">
        <EntityChange :entityChange="getEntityChangeEvent(item, 'created')" />
      </template>

      <template #item.lastModificationTime="{ item }">
        <EntityChange :entityChange="getEntityChangeEvent(item, 'modified')" />
      </template>

      <template #item.contextMenu="{ item }">
        <ContextMenu
          :actions="getContextMenuActions(item)"
          :closeOnScrollElements="tableScrollElement"
        />
      </template>
    </OverviewTable>
  </BasePage>
</template>
